import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { metodosPagoConDefaults } from "../utils/metodosPago";
import { esSoloLectura } from "../utils/roles";
import { useAuth } from "./AuthProvider";
import { db } from "../firebase";
import { resumenPorId } from "../utils/espacios";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";

const VenueContext = createContext({});

const STORAGE_KEY = "venueboard_selectedVenueId";

export const VenueProvider = ({ children }) => {
  const { userInfo } = useAuth();

  // Venue selection
  const [venues, setVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueIdState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null
  );
  const [venueLoading, setVenueLoading] = useState(true);

  // Data subscriptions
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [conceptos, setConceptos] = useState([]);
  const [conceptosLoading, setConceptosLoading] = useState(false);
  const [loads, setLoads] = useState([]);
  const [loadsLoading, setLoadsLoading] = useState(false);
  const [socialClicks, setSocialClicks] = useState([]);
  const [socialClicksLoading, setSocialClicksLoading] = useState(false);
  const [contactForms, setContactForms] = useState([]);
  const [contactFormsLoading, setContactFormsLoading] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [clienteDepositos, setClienteDepositos] = useState([]);
  const [clienteDepositosLoading, setClienteDepositosLoading] = useState(false);
  const [costos, setCostos] = useState([]);
  const [costosLoading, setCostosLoading] = useState(false);
  const [retiros, setRetiros] = useState([]);
  const [retirosLoading, setRetirosLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [gastosRecurrentes, setGastosRecurrentes] = useState([]);
  const [gastosRecurrentesLoading, setGastosRecurrentesLoading] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [metodosPagoLoading, setMetodosPagoLoading] = useState(false);
  const [archivosTipos, setArchivosTipos] = useState([]);
  const [archivosTiposLoading, setArchivosTiposLoading] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresLoading, setProveedoresLoading] = useState(false);
  const [telegramChats, setTelegramChats] = useState([]);
  const [telegramChatsLoading, setTelegramChatsLoading] = useState(false);
  const [calendarTimezone, setCalendarTimezone] = useState("America/Mexico_City");
  const [venueFeatures, setVenueFeatures] = useState({});
  const [cotizacionConfig, setCotizacionConfig] = useState({});
  // Doc del venue seleccionado, VIVO. `venues` se carga una sola vez con
  // getDocs, así que `selectedVenue.espaciosResumen` queda stale en cuanto el
  // owner edita sus espacios en otra pestaña. El rollup lo consumen el
  // calendario, leads y ocupación — vive aquí para que todos lean lo mismo.
  const [selectedVenueDoc, setSelectedVenueDoc] = useState(null);

  // Persist selectedVenueId
  const setSelectedVenueId = useCallback((id) => {
    setSelectedVenueIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Fetch venues based on role
  useEffect(() => {
    if (!userInfo) {
      setVenues([]);
      setVenueLoading(false);
      return;
    }

    const fetchVenues = async () => {
      setVenueLoading(true);
      let venuesData = [];
      const venuesCollection = collection(db, "venues");

      if (userInfo.role === "admin") {
        const snapshot = await getDocs(venuesCollection);
        venuesData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      } else if (userInfo.venues && userInfo.venues.length > 0) {
        const venuesQuery = query(
          venuesCollection,
          where("__name__", "in", userInfo.venues)
        );
        const snapshot = await getDocs(venuesQuery);
        venuesData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      }

      setVenues(venuesData);
      setVenueLoading(false);
    };

    fetchVenues();
  }, [userInfo]);

  // Auto-select venue: validate persisted ID or pick first
  useEffect(() => {
    if (venues.length === 0) return;

    if (selectedVenueId && venues.some((v) => v.id === selectedVenueId)) {
      return; // persisted ID is valid
    }

    // Pick first venue
    setSelectedVenueId(venues[0].id);
  }, [venues, selectedVenueId, setSelectedVenueId]);

  // Selected venue object
  const selectedVenue = useMemo(
    () => venues.find((v) => v.id === selectedVenueId) || null,
    [venues, selectedVenueId]
  );

  // Suscripción viva al doc del venue seleccionado (rollup de espacios).
  useEffect(() => {
    if (!selectedVenueId) {
      setSelectedVenueDoc(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, "venues", selectedVenueId),
      (snap) => setSelectedVenueDoc(snap.exists() ? snap.data() : null),
      (err) => console.error("Venue doc snapshot error:", err)
    );
    return unsub;
  }, [selectedVenueId]);

  // ── Multiespacio (FRA-293) ──────────────────────────────────────────
  // Fuente única del rollup para todo el app. Venue sin espacios ⇒ listas
  // vacías ⇒ cada consumidor se comporta como antes de multiespacio.
  const espaciosResumen = useMemo(
    () => selectedVenueDoc?.espaciosResumen || [],
    [selectedVenueDoc]
  );
  const espaciosPorId = useMemo(
    () => resumenPorId(espaciosResumen),
    [espaciosResumen]
  );
  // Multiespacio operativo es un flag ADMIN (adminConfig/features). Apagado,
  // cualquier venue puede capturar espacios para presumirlos en su ficha
  // —cocina, jardín, salón principal— pero no se reservan por separado: no
  // hay carriles de calendario, ni conflictos por salón, ni espacios de
  // interés en leads. Reservar por salón es la parte complicada y se
  // habilita venue por venue.
  const multiespacioActivo = venueFeatures?.multiespacio === true;

  // Activos Y reservables: las áreas de showcase (reservable: false) salen en
  // la ficha pública pero nunca en calendario, conflictos ni ocupación. Con
  // el flag apagado NINGUNO es reservable — esta lista vacía es lo que hace
  // que todo el app se comporte como antes de multiespacio.
  const espaciosActivos = useMemo(
    () =>
      multiespacioActivo
        ? espaciosResumen.filter(
            (e) => e.activo !== false && e.reservable !== false
          )
        : [],
    [espaciosResumen, multiespacioActivo]
  );
  const permiteEventosSimultaneos =
    selectedVenueDoc?.permiteEventosSimultaneos !== false;

  // --- Data Subscriptions ---
  // Events: venues/{id}/calendars/default/events
  useEffect(() => {
    if (!selectedVenueId) {
      setEvents([]);
      return;
    }
    setEventsLoading(true);
    const eventsCol = collection(
      db,
      "venues",
      selectedVenueId,
      "calendars",
      "default",
      "events"
    );
    const unsub = onSnapshot(eventsCol, (snapshot) => {
      setEvents(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setEventsLoading(false);
    }, (err) => {
      console.error("Events snapshot error:", err);
      setEventsLoading(false);
    });
    return unsub;
  }, [selectedVenueId]);

  const isViewer = userInfo?.role === "viewer";
  // Lo que es Configuración (Telegram, diseño de cotización) no lo lee el
  // inversionista (P-FRA-13): las reglas lo niegan, así que ni se suscribe.
  const sinConfiguracion = esSoloLectura(userInfo);

  // Payments: venues/{id}/payments
  useEffect(() => {
    if (!selectedVenueId) {
      setPayments([]);
      setPaymentsLoading(false);
      return;
    }
    setPaymentsLoading(true);
    const paymentsCol = collection(db, "venues", selectedVenueId, "payments");
    const unsub = onSnapshot(paymentsCol, (snapshot) => {
      setPayments(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPaymentsLoading(false);
    }, (err) => {
      console.error("Payments snapshot error:", err);
      setPaymentsLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // Conceptos: venues/{id}/conceptos (skip for viewers)
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setConceptos([]);
      setConceptosLoading(false);
      return;
    }
    setConceptosLoading(true);
    const conceptosCol = collection(db, "venues", selectedVenueId, "conceptos");
    const unsub = onSnapshot(conceptosCol, (snapshot) => {
      setConceptos(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setConceptosLoading(false);
    }, (err) => {
      console.error("Conceptos snapshot error:", err);
      setConceptosLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // Loads: venues/{id}/loads (ordered by timestamp desc, limit 1000)
  useEffect(() => {
    if (!selectedVenueId) {
      setLoads([]);
      return;
    }
    setLoadsLoading(true);
    const loadsCol = collection(db, "venues", selectedVenueId, "loads");
    const loadsQuery = query(loadsCol, orderBy("timestamp", "desc"), limit(1000));
    const unsub = onSnapshot(loadsQuery, (snapshot) => {
      setLoads(snapshot.docs.map((d) => d.data()));
      setLoadsLoading(false);
    }, (err) => {
      console.error("Loads snapshot error:", err);
      setLoadsLoading(false);
    });
    return unsub;
  }, [selectedVenueId]);

  // Social Clicks: venues/{id}/socialClicks
  useEffect(() => {
    if (!selectedVenueId) {
      setSocialClicks([]);
      return;
    }
    setSocialClicksLoading(true);
    const socialClicksCol = collection(db, "venues", selectedVenueId, "socialClicks");
    const unsub = onSnapshot(socialClicksCol, (snapshot) => {
      setSocialClicks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSocialClicksLoading(false);
    }, (err) => {
      console.error("SocialClicks snapshot error:", err);
      setSocialClicksLoading(false);
    });
    return unsub;
  }, [selectedVenueId]);

  // Formularios (leads): venueContactForms filtrados por venueId. Los usa el
  // % de contacto de Analíticas web (Home), "Leads por atender" y Reportes.
  // Los leads borrados desde el pipeline son soft-delete (`deleted: true`,
  // conservan su `status`) y AQUÍ SE CONSERVAN a propósito: Analíticas web y
  // Reportes cuentan formularios recibidos, y borrar un duplicado o un spam
  // del pipeline no puede restarle visitas convertidas al pasado. Quien
  // muestre leads como pendientes de trabajo (Home → Leads por atender, el
  // kanban) filtra `deleted` él mismo.
  useEffect(() => {
    if (!selectedVenueId) {
      setContactForms([]);
      return;
    }
    setContactFormsLoading(true);
    const formsQuery = query(
      collection(db, "venueContactForms"),
      where("venueId", "==", selectedVenueId)
    );
    const unsub = onSnapshot(formsQuery, (snapshot) => {
      setContactForms(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setContactFormsLoading(false);
    }, (err) => {
      console.error("ContactForms snapshot error:", err);
      setContactFormsLoading(false);
    });
    return unsub;
  }, [selectedVenueId]);

  // Refunds: venues/{id}/refunds (skip for viewers)
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setRefunds([]);
      setRefundsLoading(false);
      return;
    }
    setRefundsLoading(true);
    const refundsCol = collection(db, "venues", selectedVenueId, "refunds");
    const unsub = onSnapshot(refundsCol, (snapshot) => {
      setRefunds(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setRefundsLoading(false);
    }, (err) => {
      console.error("Refunds snapshot error:", err);
      setRefundsLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // ClienteDepositos: venues/{id}/clienteDepositos (skip for viewers)
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setClienteDepositos([]);
      setClienteDepositosLoading(false);
      return;
    }
    setClienteDepositosLoading(true);
    const depositosCol = collection(db, "venues", selectedVenueId, "clienteDepositos");
    const unsub = onSnapshot(depositosCol, (snapshot) => {
      setClienteDepositos(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setClienteDepositosLoading(false);
    }, (err) => {
      console.error("ClienteDepositos snapshot error:", err);
      setClienteDepositosLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // Costos: venues/{id}/costos (skip for viewers). Espejo de payments: la
  // colección es plana y el evento vive como campo `eventId`, que además
  // puede ser null (gasto general del venue).
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setCostos([]);
      setCostosLoading(false);
      return;
    }
    setCostosLoading(true);
    const costosCol = collection(db, "venues", selectedVenueId, "costos");
    const unsub = onSnapshot(costosCol, (snapshot) => {
      setCostos(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCostosLoading(false);
    }, (err) => {
      console.error("Costos snapshot error:", err);
      setCostosLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // Retiros del dueño: venues/{id}/retiros (skip for viewers). Colección
  // propia a propósito (FRA-343): un retiro es repartición de utilidades, no
  // un costo, y así no puede colarse en un evento ni en un reporte de
  // resultados. Solo el ledger (Cuentas, Flujo de caja) la lee.
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setRetiros([]);
      setRetirosLoading(false);
      return;
    }
    setRetirosLoading(true);
    const retirosCol = collection(db, "venues", selectedVenueId, "retiros");
    const unsub = onSnapshot(retirosCol, (snapshot) => {
      setRetiros(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setRetirosLoading(false);
    }, (err) => {
      console.error("Retiros snapshot error:", err);
      setRetirosLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // Clientes del venue: venues/{id}/clientes (FRA-354, skip for viewers).
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setClientes([]);
      setClientesLoading(false);
      return;
    }
    setClientesLoading(true);
    const col = collection(db, "venues", selectedVenueId, "clientes");
    const unsub = onSnapshot(col, (snapshot) => {
      setClientes(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setClientesLoading(false);
    }, (err) => {
      console.error("Clientes snapshot error:", err);
      setClientesLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // Gastos recurrentes: venues/{id}/gastosRecurrentes (FRA-356, skip viewers).
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setGastosRecurrentes([]);
      setGastosRecurrentesLoading(false);
      return;
    }
    setGastosRecurrentesLoading(true);
    const col = collection(db, "venues", selectedVenueId, "gastosRecurrentes");
    const unsub = onSnapshot(col, (snapshot) => {
      setGastosRecurrentes(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setGastosRecurrentesLoading(false);
    }, (err) => {
      console.error("GastosRecurrentes snapshot error:", err);
      setGastosRecurrentesLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // On-demand loader for proveedor depositos. Returns a flat array of all
  // depositos across the venue's proveedores, each enriched with proveedorId
  // and proveedorNombre. Not subscribed live — call when the consumer needs
  // a snapshot (e.g., when the Depósitos tab opens). Re-runs on every call.
  const loadProveedorDepositos = useCallback(async () => {
    if (!selectedVenueId || !proveedores.length) return [];
    const groups = await Promise.all(
      proveedores.map(async (prov) => {
        const depCol = collection(
          db, "venues", selectedVenueId, "proveedores", prov.id, "depositos"
        );
        const snap = await getDocs(depCol);
        return snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          proveedorId: prov.id,
          proveedorNombre: prov.nombre || "",
        }));
      })
    );
    return groups.flat();
  }, [selectedVenueId, proveedores]);

  // On-demand loader para archivos internos de un evento. La subcolección
  // events/{eid}/archivos no se puede listar para viewers (rules lo bloquean),
  // así que ni el archivo ni la pestaña existen para ellos.
  const loadEventArchivos = useCallback(async (calendarId, eventId) => {
    if (!selectedVenueId || !calendarId || !eventId || isViewer) return [];
    const archivosCol = collection(
      db, "venues", selectedVenueId, "calendars", calendarId, "events", eventId, "archivos"
    );
    const snap = await getDocs(archivosCol);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }, [selectedVenueId, isViewer]);

  // Tipos custom de archivos del venue: venues/{id}/settings/archivosTipos.tipos[]
  useEffect(() => {
    if (!selectedVenueId) {
      setArchivosTipos([]);
      setArchivosTiposLoading(false);
      return;
    }
    setArchivosTiposLoading(true);
    const docRef = doc(db, "venues", selectedVenueId, "settings", "archivosTipos");
    const unsub = onSnapshot(docRef, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setArchivosTipos(Array.isArray(data.tipos) ? data.tipos : []);
      setArchivosTiposLoading(false);
    }, (err) => {
      console.error("ArchivosTipos snapshot error:", err);
      setArchivosTiposLoading(false);
    });
    return unsub;
  }, [selectedVenueId]);

  // MetodosPago: venues/{id}/metodosPago (skip for viewers)
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setMetodosPago([]);
      setMetodosPagoLoading(false);
      return;
    }
    setMetodosPagoLoading(true);
    const metodosPagoCol = collection(db, "venues", selectedVenueId, "metodosPago");
    const unsub = onSnapshot(metodosPagoCol, (snapshot) => {
      setMetodosPago(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMetodosPagoLoading(false);
    }, (err) => {
      console.error("MetodosPago snapshot error:", err);
      setMetodosPagoLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // La lista que consumen las pantallas de captura: la colección + Efectivo
  // y Transferencia. `metodosPago` (cruda) queda para MetodosPagoManager.
  const metodosPagoConDefaultsLista = useMemo(
    () => metodosPagoConDefaults(metodosPago),
    [metodosPago]
  );

  // Proveedores: venues/{id}/proveedores (skip for viewers — internal data)
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setProveedores([]);
      setProveedoresLoading(false);
      return;
    }
    setProveedoresLoading(true);
    const proveedoresCol = collection(db, "venues", selectedVenueId, "proveedores");
    const unsub = onSnapshot(proveedoresCol, (snapshot) => {
      setProveedores(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setProveedoresLoading(false);
    }, (err) => {
      console.error("Proveedores snapshot error:", err);
      setProveedoresLoading(false);
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // TelegramChats: venues/{id}/telegramChats (skip viewer e inversionista — config operativa)
  useEffect(() => {
    if (!selectedVenueId || sinConfiguracion) {
      setTelegramChats([]);
      setTelegramChatsLoading(false);
      return;
    }
    setTelegramChatsLoading(true);
    const telegramChatsCol = collection(db, "venues", selectedVenueId, "telegramChats");
    const unsub = onSnapshot(telegramChatsCol, (snapshot) => {
      setTelegramChats(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTelegramChatsLoading(false);
    }, (err) => {
      console.error("TelegramChats snapshot error:", err);
      setTelegramChatsLoading(false);
    });
    return unsub;
  }, [selectedVenueId, sinConfiguracion]);

  // Venue features: venues/{id}/adminConfig/features (admin-only writes)
  useEffect(() => {
    if (!selectedVenueId || isViewer) {
      setVenueFeatures({});
      return;
    }
    const featuresDocRef = doc(db, "venues", selectedVenueId, "adminConfig", "features");
    const unsub = onSnapshot(featuresDocRef, (snap) => {
      setVenueFeatures(snap.exists() ? snap.data() : {});
    }, (err) => {
      console.error("VenueFeatures snapshot error:", err);
      setVenueFeatures({});
    });
    return unsub;
  }, [selectedVenueId, isViewer]);

  // Cotización config: venues/{id}/cotizacionConfig/settings
  useEffect(() => {
    if (!selectedVenueId || sinConfiguracion) {
      setCotizacionConfig({});
      return;
    }
    const configDocRef = doc(db, "venues", selectedVenueId, "cotizacionConfig", "settings");
    const unsub = onSnapshot(configDocRef, (snap) => {
      setCotizacionConfig(snap.exists() ? snap.data() : {});
    }, (err) => {
      console.error("CotizacionConfig snapshot error:", err);
      setCotizacionConfig({});
    });
    return unsub;
  }, [selectedVenueId, sinConfiguracion]);

  // Calendar timezone: venues/{id}/calendars/default (single doc via getDoc)
  useEffect(() => {
    if (!selectedVenueId) {
      setCalendarTimezone("America/Mexico_City");
      return;
    }
    const fetchTimezone = async () => {
      try {
        const calendarDocRef = doc(
          db,
          "venues",
          selectedVenueId,
          "calendars",
          "default"
        );
        const snap = await getDoc(calendarDocRef);
        if (snap.exists() && snap.data().timezone) {
          setCalendarTimezone(snap.data().timezone);
        } else {
          setCalendarTimezone("America/Mexico_City");
        }
      } catch {
        setCalendarTimezone("America/Mexico_City");
      }
    };
    fetchTimezone();
  }, [selectedVenueId]);

  const value = useMemo(
    () => ({
      venues,
      selectedVenueId,
      setSelectedVenueId,
      selectedVenue,
      venueLoading,
      events,
      eventsLoading,
      payments,
      paymentsLoading,
      conceptos,
      conceptosLoading,
      loads,
      loadsLoading,
      socialClicks,
      socialClicksLoading,
      contactForms,
      contactFormsLoading,
      refunds,
      refundsLoading,
      clienteDepositos,
      clienteDepositosLoading,
      costos,
      costosLoading,
      retiros,
      retirosLoading,
      clientes,
      clientesLoading,
      gastosRecurrentes,
      gastosRecurrentesLoading,
      loadProveedorDepositos,
      loadEventArchivos,
      metodosPago,
      metodosPagoConDefaults: metodosPagoConDefaultsLista,
      metodosPagoLoading,
      archivosTipos,
      archivosTiposLoading,
      proveedores,
      proveedoresLoading,
      telegramChats,
      telegramChatsLoading,
      calendarTimezone,
      venueFeatures,
      cotizacionConfig,
      espaciosResumen,
      espaciosActivos,
      espaciosPorId,
      permiteEventosSimultaneos,
      multiespacioActivo,
    }),
    [
      venues,
      selectedVenueId,
      setSelectedVenueId,
      selectedVenue,
      venueLoading,
      events,
      eventsLoading,
      payments,
      paymentsLoading,
      conceptos,
      conceptosLoading,
      loads,
      loadsLoading,
      socialClicks,
      socialClicksLoading,
      contactForms,
      contactFormsLoading,
      refunds,
      refundsLoading,
      clienteDepositos,
      clienteDepositosLoading,
      costos,
      costosLoading,
      retiros,
      retirosLoading,
      clientes,
      clientesLoading,
      gastosRecurrentes,
      gastosRecurrentesLoading,
      loadProveedorDepositos,
      loadEventArchivos,
      metodosPago,
      metodosPagoConDefaultsLista,
      metodosPagoLoading,
      archivosTipos,
      archivosTiposLoading,
      proveedores,
      proveedoresLoading,
      telegramChats,
      telegramChatsLoading,
      calendarTimezone,
      venueFeatures,
      cotizacionConfig,
      espaciosResumen,
      espaciosActivos,
      espaciosPorId,
      permiteEventosSimultaneos,
      multiespacioActivo,
    ]
  );

  return (
    <VenueContext.Provider value={value}>{children}</VenueContext.Provider>
  );
};

export const useVenue = () => useContext(VenueContext);
